import type {
  RumActionEvent,
  RumActionEventDomainContext,
  RumEvent,
  RumEventDomainContext,
  RumInitConfiguration,
  RumGlobal as RumPublicApi
} from "@datadog/browser-rum";
import type Router from "@ember/routing/router";
import type RouterService from "@ember/routing/router-service";
import type Transition from "@ember/routing/transition";
import ENV from "parog-web/config/environment";
import {
  getEnvironmentNameFromHostname,
  getServiceUrl
} from "parog-web/utils/runtime-environment";
import { maskQueryParamsFromUrl } from "wise-control-tower";

const QUERY_PARAMS_WHITELIST = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content"
];

/** This function modifies the URLs a RumResourceEvent or RumViewEvent to avoid
 * RGPD info leaks
 *
 * Here's the doc page for event types
 * https://docs.datadoghq.com/fr/real_user_monitoring/browser/data_collected#section-overview
 */
function maskEventQueryParams(event: RumEvent) {
  if (event.type === "resource") {
    event.resource.url = maskQueryParamsFromUrl(event.resource.url);
  } else if (event.type === "view") {
    event.view.url = maskQueryParamsFromUrl(
      event.view.url,
      QUERY_PARAMS_WHITELIST
    );
  }
}

function isNewVersionEvent(event: RumEvent): boolean {
  if (!event.resource) return false;

  const url = new URL(event.resource["url"]);

  return url.pathname === "/VERSION.txt";
}

/**
 * Sets the action target name to the first "data-test-" attribute of the target
 * element if the original target name is "Masked Element"
 */
function setActionTargetNameWithDataTestAttribute(
  event: RumEvent,
  context: RumEventDomainContext
) {
  /* Predicate function to type event and context based on the event type */
  function isActionEvent(eventWithContext: {
    event: RumEvent;
    context: RumEventDomainContext;
  }): eventWithContext is {
    event: RumActionEvent;
    context: RumActionEventDomainContext;
  } {
    return eventWithContext.event.type === "action";
  }

  /* Early return if the event is not an action event, since only action events
  have a "target" property that we want to set */
  const eventWithContext = { event, context };
  if (!isActionEvent(eventWithContext)) return;

  /* extract the event and context with proper typing */
  const { event: e, context: c } = eventWithContext;

  /* Early return if the action has no target */
  if (!e.action.target) return;

  /* Early return if the target name is not ACTION_NAME_PLACEHOLDER. This means
  this specific action target was allowed

  ACTION_NAME_PLACEHOLDER is defined in @datadog/browser-rum-core but not public
  so we have to hardcode it here.
  */
  const ACTION_NAME_PLACEHOLDER = "Masked Element";
  if (e.action.target.name != ACTION_NAME_PLACEHOLDER) return;

  /* Extract the target element from the context and early return if it's not an
  Element since we need to read its attributes */
  const element = c.events ? c.events[0]?.target : undefined;
  if (!(element instanceof Element)) return;

  /* find the first "data-test-" attribute and early return if not found */
  const attr = [...element.attributes].find(attr =>
    attr.name.startsWith("data-test-")
  );
  if (!attr) return;

  /* Finally set the event's action target name */
  e.action.target.name = attr.name;
}

const GENERIC_ID_SEGMENT = ":id";

export class RumInitializer {
  /** The name of the current Datadog view
   * It is set at the initial load with the URL pathname and then updated
   * at every route change with the new URL pathname
   *
   * We need to keep track of it to avoid starting new DD views when
   * the URL pathname does not change
   * (for example when only the query params change)
   */
  currentViewName: string | undefined;
  datadogRum: RumPublicApi | undefined;
  router: RouterService;
  routerMain: Router;

  constructor(appInstance) {
    this.router = appInstance.lookup("service:router");

    /*
    eslint-disable-next-line ember/no-private-routing-service --

    router:main is a private API and it is not recommended to use it,
    but we need it here to access the current RouteInfo during an initial load

    The routerService.currentRoute would have been useful here, but it will only
    be set after the routeDidChange event, and we need this info before to properly
    start a Datadog View
    */
    this.routerMain = appInstance.lookup("router:main");
  }

  initialize = async () => {
    const clientToken = ENV.DD_RUM_CLIENT_TOKEN;
    const applicationId = ENV.DD_RUM_APPLICATION_ID;

    // We need both clientToken and applicationId!
    if (!clientToken || !applicationId) return;

    const env = getEnvironmentNameFromHostname(window.location.hostname);

    const initConfig: RumInitConfiguration = {
      clientToken,
      applicationId,
      site: "datadoghq.eu",
      service: "wise",
      env,
      version: `${ENV.APP["version"]}`,

      /*
    Sessions will be retained based on the retention filters setup in the RUM interface
    See https://app.datadoghq.eu/rum/application/f53e0bcb-45d3-4069-ba9a-9380f6bef839/retention-filters
    */
      sessionSampleRate: 100,

      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,

      // See https://docs.datadoghq.com/fr/data_security/real_user_monitoring/#mask-action-names
      enablePrivacyForActionName: true,

      defaultPrivacyLevel: "mask",
      storeContextsAcrossPages: true,
      allowedTracingUrls: [
        /https:\/\/.*\.semeia\.io\/api/,
        /http:\/\/localhost(:\d+)?\/api/
      ],
      traceSampleRate: 100,

      // https://docs.datadoghq.com/fr/real_user_monitoring/browser/advanced_configuration/?tab=npm
      trackViewsManually: true,

      beforeSend: (event: RumEvent, context: RumEventDomainContext) => {
        maskEventQueryParams(event);
        setActionTargetNameWithDataTestAttribute(event, context);

        if (isNewVersionEvent(event)) return false;

        /*
      Reject view events with a referrer that contains something that looks like a sensitive token

      For example, if you are on a page http://wise.semeia.io?foo=bar and reload the page with cmd+R
      The next view event sent to DD would contain "http://wise.semeia.io?foo=bar" as the referrer

      Likewise, this would happen if you navigate from the URL http://wise.semeia.io?foo=bar.
      Once again, the navigation view event sent to DD would contain "http://wise.semeia.io?foo=bar" as the referrer

      We do not want to drop all view events with a referrer though, we would loose too much data,
      so we choose to only drop view events with a referrer containing "token="
      */
        if (event.view?.referrer?.includes("token=")) return false;

        return true;
      }
    };

    // For the "local" env, we do not want to use a RUM proxy
    if (env !== "local")
      initConfig.proxy = options =>
        `${getServiceUrl("wise")}${options.path}?${options.parameters}`;

    // Whitelist envs with RUM and early return if the env is not valid for RUM
    const validEnvironments = [
      "local",
      "master",
      "release",
      "preproduction",
      "production"
    ];
    if (!validEnvironments.includes(env)) return;

    // If we reach this point, we are in a valid RUM environment. Let's set it up
    const { datadogRum } = await import("@datadog/browser-rum");
    this.datadogRum = datadogRum;
    datadogRum.init(initConfig);

    return;
  };

  /**
   * Starts a DD view when this initializer is loaded, at page load time
   * This method WILL NOT be called when transitionning to another view
   *
   * The corresponding DD view "loading_type" property will be "initial_load"
   */
  startInitialLoadView() {
    // Get the Location object and return if it is only a string
    const location = this.routerMain.location;
    if (typeof location === "string") return;

    /* Strip query params from the URL to avoid:
    - sending sensitive query params to DD
    - creating too many different views in DD
    */
    const pathname = new URL(location.getURL(), "https://dummy.base").pathname;
    this.startView(pathname);
  }

  /**
   * Starts a DD view for non intermediate transitions
   */
  startRouteChangeView = ({ to, isIntermediate }: Transition) => {
    /*
    If the target route is undefined, we do not want to create a new view
    This should not happen in theory…
    */
    if (!to) return;

    /*
    We do not want to create new views for "loading" or "error" substates;
    but let's add a custom DD timing
    */
    if (isIntermediate) {
      this.datadogRum?.addTiming(
        to.name ?? "missing intermediate transition name"
      );
      return;
    }

    this.startView(this.urlForRouteInfo(to));
  };

  /**
   * Instrument the Ember router to properly start DD views
   */
  trackViewsManually = () => {
    this.startInitialLoadView();
    this.router.on("routeWillChange", this.startRouteChangeView);

    this.router.on("routeDidChange", (transition: Transition) => {
      /*
      Add a custom timing information to mark the "routeDidChange" event
      every beforeModel/model/afterModel are finished

      At that point in time though,
      the JS work is not finished yet and no painting has happened
      */
      this.datadogRum?.addTiming("routeDidChange");

      /*
      Start a new DD view for the route change

      The corresponding DD view "loading_type" property will be "route_change"
      */
      this.startRouteChangeView(transition);
    });
  };

  private startView(pathname: string) {
    /* Replace any numerical part of the pathname with ":id" to avoid creating
    too many different views in DD */
    const genericPathname = pathname.replace(/\d+/g, GENERIC_ID_SEGMENT);

    // If the URL pathname did not change, we do not want to start a new DD view
    if (genericPathname === this.currentViewName) return;

    this.currentViewName = genericPathname;

    if (this.datadogRum) {
      performance.mark(`RUM startView: ${this.currentViewName}`);
      this.datadogRum.startView({ name: this.currentViewName });
    }
  }

  /**
   * Finds the URL corresponding to a RouteInfo object like "transition.to"
   */
  private urlForRouteInfo(to: NonNullable<Transition["to"]>) {
    const paramValues: unknown[] = [];
    let routeInfo: typeof to | null = to;

    // To find the dynamic segment values, we have to traverse up the route hierarchy
    while (routeInfo) {
      const { paramNames, params } = routeInfo;

      paramValues.unshift(
        ...(paramNames ?? []).map(
          paramName => params?.[paramName] ?? GENERIC_ID_SEGMENT
        )
      );

      routeInfo = routeInfo.parent;
    }

    return this.router.urlFor(to.name, ...paramValues);
  }
}

export async function initialize(appInstance): Promise<void> {
  const rumInitializer = new RumInitializer(appInstance);
  await rumInitializer.initialize();
  if (rumInitializer.datadogRum) rumInitializer.trackViewsManually();
}

export default {
  initialize
};
